import { initializeApp, getApps } from 'firebase/app'
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getMessaging, getToken, onMessage, type MessagePayload, type Messaging } from 'firebase/messaging'
import { getCurrentEnvironment, subscribeToEnvironment } from './environmentStore'

type FirebaseDebugWindow = Window & {
  __fcmDebug?: Record<string, unknown>
  __firebaseAuth?: Auth
  getFirebaseIdToken?: () => Promise<string | null>
}

const env = import.meta.env
const firebaseDefaults = {
  apiKey: 'AIzaSyD_kLUPYUJB7XI7zlIwZc-o0AdlfQnlsdA',
  authDomain: 'ultron-turing-project.firebaseapp.com',
  projectId: 'ultron-turing-project',
  storageBucket: 'ultron-turing-project.firebasestorage.app',
  messagingSenderId: '1051579702053',
  appId: '1:1051579702053:web:ac74db352d72b289d398cc',
  measurementId: 'G-W4VKLDGYDB'
}
const firebaseConfig = {
  apiKey: (env.VITE_FIREBASE_API_KEY as string) || firebaseDefaults.apiKey,
  authDomain: (env.VITE_FIREBASE_AUTH_DOMAIN as string) || firebaseDefaults.authDomain,
  projectId: (env.VITE_FIREBASE_PROJECT_ID as string) || firebaseDefaults.projectId,
  storageBucket: (env.VITE_FIREBASE_STORAGE_BUCKET as string) || firebaseDefaults.storageBucket,
  messagingSenderId: (env.VITE_FIREBASE_MESSAGING_SENDER_ID as string) || firebaseDefaults.messagingSenderId,
  appId: (env.VITE_FIREBASE_APP_ID as string) || firebaseDefaults.appId,
  measurementId: (env.VITE_FIREBASE_MEASUREMENT_ID as string) || firebaseDefaults.measurementId, // Optional, for Analytics
}

const requiredKeys = [
  firebaseConfig.apiKey,
  firebaseConfig.authDomain,
  firebaseConfig.projectId,
  firebaseConfig.storageBucket,
  firebaseConfig.messagingSenderId,
  firebaseConfig.appId,
]

export const firebaseEnabled = requiredKeys.every((value) => Boolean(value))

const app = firebaseEnabled
  ? getApps().length
    ? getApps()[0]
    : initializeApp(firebaseConfig)
  : null

export const auth: Auth | null = app ? getAuth(app) : null

export function getExpectedFirestoreDatabaseId(): 'staging' | '(default)' {
  const environment = getCurrentEnvironment()
  return environment === 'staging' ? 'staging' : '(default)'
}

function resolveFirestoreDatabaseId(): '(default)' | 'staging' {
  return getExpectedFirestoreDatabaseId()
}

export let resolvedFirestoreDatabaseId: '(default)' | 'staging' = resolveFirestoreDatabaseId()
export let firestore: Firestore | null = null

function syncFirestoreWithEnvironment(): void {
  const nextDatabaseId = resolveFirestoreDatabaseId()
  resolvedFirestoreDatabaseId = nextDatabaseId
  firestore = app
    ? (nextDatabaseId === 'staging' ? getFirestore(app, 'staging') : getFirestore(app))
    : null
}

syncFirestoreWithEnvironment()

if (typeof window !== 'undefined') {
  subscribeToEnvironment(() => {
    syncFirestoreWithEnvironment()
  })
}

export function assertFirestoreEnvironmentForWrite(scope = 'firestore write'): void {
  if (typeof window === 'undefined') return
  const expectedDb = getExpectedFirestoreDatabaseId()
  if (resolvedFirestoreDatabaseId !== expectedDb) {
    throw new Error(
      `[DB Guard] ${scope} blocked: expected Firestore DB "${expectedDb}" from UI environment, but active DB is "${resolvedFirestoreDatabaseId}".`
    )
  }
}

export const googleProvider = firebaseEnabled ? new GoogleAuthProvider() : null

// Firebase Messaging (for FCM push notifications)
export const messaging: Messaging | null = app && typeof window !== 'undefined' && 'Notification' in window
  ? getMessaging(app)
  : null

// VAPID Key for web push (from Firebase Console > Cloud Messaging > Web Push certificates)
// Source priority: 1) runtime (set by UI from backend), 2) env VITE_FIREBASE_VAPID_KEY
let runtimeVapidKey = ''
export function setFcmVapidKey(key: string): void {
  runtimeVapidKey = String(key || '').trim()
}
export function getFcmVapidKey(): string {
  return runtimeVapidKey || (env.VITE_FIREBASE_VAPID_KEY as string || '')
}

// No load-time warning - subscribeFCM will error clearly if key missing when called

/**
 * Subscribe to FCM push notifications
 * Wait for Firebase's Service Worker, then get token from that specific SW
 * @param vapidKeyOverride - Optional. Use this if caller has VAPID from form/backend (e.g. Firebase Configured modal)
 */
export async function subscribeFCM(vapidKeyOverride?: string): Promise<string | null> {
  console.log('=== subscribeFCM v3 ===')
  
  if (!messaging) {
    console.error('❌ Firebase Messaging not available - check if browser supports notifications')
    return null
  }

  const vapidKey = (vapidKeyOverride && vapidKeyOverride.trim()) || getFcmVapidKey()
  if (!vapidKey || vapidKey.trim() === '') {
    console.error('❌ FCM VAPID_KEY is missing or empty!')
    console.error('   Save VAPID in Firebase Configured modal, or set VITE_FIREBASE_VAPID_KEY in .env')
    throw new Error('VAPID_KEY is required for FCM. Save it in Firebase Configured modal or set VITE_FIREBASE_VAPID_KEY in .env')
  }

  try {
    // Request notification permission
    const permission = await Notification.requestPermission()
    console.log('📱 Notification permission:', permission)
    
    if (permission !== 'granted') {
      console.warn('⚠️ Notification permission denied. FCM will not work.')
      return null
    }

    // Get token first - this creates Firebase's Service Worker
    console.log('🔑 Getting FCM token with VAPID:', vapidKey.substring(0, 20) + '...')
    
    let fcmToken: string | null = null
    try {
      fcmToken = await getToken(messaging, { vapidKey })
      console.log('✅ FCM getToken returned:', fcmToken ? `${fcmToken.substring(0, 40)}...` : 'null')
    } catch (tokenError: unknown) {
      console.error('❌ Failed to get FCM token:', tokenError)
      const tokenErrorCode =
        typeof tokenError === 'object' &&
        tokenError !== null &&
        'code' in tokenError
          ? String((tokenError as { code?: unknown }).code || '')
          : ''
      const tokenErrorMessage =
        tokenError instanceof Error ? tokenError.message : String(tokenError ?? '')
      
      // Provide helpful error messages
      if (tokenErrorCode === 'messaging/invalid-vapid-key') {
        throw new Error('Invalid VAPID key. Please check VITE_FIREBASE_VAPID_KEY in .env file.')
      } else if (tokenErrorCode === 'messaging/permission-blocked') {
        throw new Error('Notification permission is blocked. Please enable notifications in browser settings.')
      } else if (tokenErrorCode === 'messaging/unsupported-browser') {
        throw new Error('This browser does not support FCM. Please use a modern browser.')
      } else {
        throw new Error(`Failed to get FCM token: ${tokenErrorMessage || tokenErrorCode || 'Unknown error'}`)
      }
    }
    
    if (!fcmToken) {
      console.error('❌ FCM token is null')
      return null
    }
    
    console.log('✅ FCM Token successfully obtained:', fcmToken.substring(0, 40) + '...')
    
    // Verify Service Worker is registered (required for background notifications)
    const regs = await navigator.serviceWorker.getRegistrations()
    console.log('🔧 Service Workers found:', regs.length)
    
    let fcmServiceWorkerFound = false
    for (const reg of regs) {
      console.log('   SW scope:', reg.scope)
      // Check if this is the Firebase Messaging Service Worker
      if (reg.scope.includes('firebase-cloud-messaging') || reg.active?.scriptURL?.includes('firebase-messaging')) {
        fcmServiceWorkerFound = true
        console.log('✅ Firebase Messaging Service Worker found')
        
        // Check push subscription (for debugging, but not required for FCM to work)
        const sub = await reg.pushManager.getSubscription()
        if (sub) {
          console.log('   Push subscription active:', sub.endpoint.substring(0, 50) + '...')
        } else {
          console.log('   Push subscription: Will be created when needed')
        }
        break
      }
    }
    
    if (!fcmServiceWorkerFound) {
      console.warn('⚠️ Firebase Messaging Service Worker not found, but FCM token is valid')
      console.warn('   Background notifications may not work, but foreground notifications should work')
    }
    
    return fcmToken
  } catch (error: unknown) {
    console.error('❌ subscribeFCM error:', error)
    // Re-throw with helpful message
    if (error instanceof Error && error.message) {
      throw error
    }
    throw new Error(`FCM subscription failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Listen for foreground FCM messages
 * This is called when the app is in the foreground
 */
export function onFCMMessage(callback: (payload: MessagePayload) => void): () => void {
  if (!messaging) {
    console.warn('⚠️ onFCMMessage: messaging is null - FCM not available')
    return () => {}
  }
  
  console.log('✅ Setting up FCM foreground message listener...')
  console.log('   Messaging instance:', messaging)
  console.log('   Service Worker support:', 'serviceWorker' in navigator)
  
  // Set up the onMessage listener
  const unsubscribe = onMessage(messaging, (payload) => {
    console.log('🔔🔔🔔 FCM FOREGROUND MESSAGE RECEIVED! 🔔🔔🔔')
    console.log('   Timestamp:', new Date().toISOString())
    console.log('   Payload keys:', Object.keys(payload))
    console.log('   Full payload:', JSON.stringify(payload, null, 2))
    console.log('   Notification:', payload.notification)
    console.log('   Data:', payload.data)
    console.log('   Message ID:', payload.messageId)
    console.log('   From:', payload.from)
    
    try {
      callback(payload)
      console.log('   ✅ Callback executed successfully')
    } catch (error) {
      console.error('   ❌ Error in FCM message callback:', error)
      console.error('   Error stack:', error.stack)
    }
  })
  
  console.log('✅ FCM foreground message listener registered')
  console.log('   Unsubscribe function:', typeof unsubscribe)
  
  // Also set up a global listener for debugging
  if (typeof window !== 'undefined') {
    const debugWindow = window as FirebaseDebugWindow
    debugWindow.__fcmDebug = {
      messaging,
      checkListener: () => {
        console.log('🔍 FCM Debug Info:')
        console.log('  Messaging instance:', messaging)
        console.log('  Notification permission:', Notification.permission)
        console.log('  Service Worker support:', 'serviceWorker' in navigator)
        
        navigator.serviceWorker.getRegistrations().then(regs => {
          console.log('  Service Workers found:', regs.length)
          regs.forEach((reg, idx) => {
            console.log(`    SW #${idx + 1}:`, reg.scope)
            reg.pushManager.getSubscription().then(sub => {
              if (sub) {
                console.log(`      Push subscription: Active`)
                console.log(`      Endpoint:`, sub.endpoint.substring(0, 60) + '...')
              } else {
                console.log(`      Push subscription: None (will be created when needed)`)
              }
            })
          })
        })
        
        // Check if onMessage listener is set up
        console.log('  ✅ onMessage listener should be active')
        console.log('  💡 Tip: Send a test notification and check for "FCM FOREGROUND MESSAGE RECEIVED" log')
      },
      testNotification: () => {
        console.log('🧪 Testing local notification...')
        if (Notification.permission === 'granted') {
          new Notification('Debug Test', {
            body: 'If you see this, browser notifications work!',
            icon: '/ultron-logo.svg'
          })
          console.log('✅ Local notification sent')
        } else {
          console.warn('⚠️ Notification permission not granted')
        }
      }
    }
    console.log('✅ FCM debug tools available: window.__fcmDebug.checkListener() or window.__fcmDebug.testNotification()')
  }
  
  return unsubscribe
}

// Expose auth to window for debugging/testing (browser console access)
if (typeof window !== 'undefined' && auth) {
  const authInstance = auth // Capture auth in a const to avoid closure issues
  const debugWindow = window as FirebaseDebugWindow
  debugWindow.__firebaseAuth = authInstance
  // Also expose a helper function to get ID token
  debugWindow.getFirebaseIdToken = async () => {
    const user = authInstance.currentUser
    if (!user) {
      console.error('No user logged in')
      return null
    }
    const token = await user.getIdToken()
    console.log('Firebase ID Token:', token)
    return token
  }
}
